import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Users, Bell, ShoppingBag, Download, LogOut, Lock, Lightbulb, Trash2, Mic, Rocket, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const TABS = [
  { key: "subscribers", label: "Newsletter", icon: Users, statKey: "subscribers" },
  { key: "waitlist", label: "Curl Edit Waitlist", icon: Bell, statKey: "waitlist" },
  { key: "orders", label: "Orders", icon: ShoppingBag, statKey: "orders" },
  { key: "suggestions", label: "Suggestions", icon: Lightbulb, statKey: "suggestions" },
  { key: "host-offers", label: "Host Offers", icon: Mic, statKey: "host_offers" },
];

const LAUNCH_DRAFTS = {
  newsletter: {
    label: "Newsletter launch",
    emailsFrom: "subscribers",
    subject: "It's here — the first Justjjax newsletter 🌊",
    body: `Hi there,

You signed up a little while back, and I'm so pleased to say it's finally here — the very first Justjjax newsletter.

Expect curl care tips, salon news and events, little wellness notes and the odd bit of sea-swimming inspiration — all the good stuff, straight to your inbox and nothing more.

Thank you for being one of the first to join. It means the world to be building this community with you.

Have a browse of what's new over on the website, and I'll see you in the next one.

See you in the sea,
Jax x`,
  },
  curledit: {
    label: "The Curl Edit launch",
    emailsFrom: "waitlist",
    subject: "The Curl Edit is live — you're in first 🌀",
    body: `Hi there,

You joined the waitlist, so you get first dibs — The Curl Edit is officially live.

It's your personal curl-care companion: tailored routines, product guidance and tips built around your hair. Jump in and give it a go whenever you're ready.

Thank you for your patience and for being one of the very first.

See you in the sea,
Jax x`,
  },
};

function toCSV(rows) {
  if (!rows.length) return "";
  const cols = Array.from(rows.reduce((set, r) => {
    Object.keys(r).forEach((k) => set.add(k));
    return set;
  }, new Set()));
  const esc = (v) => {
    if (v == null) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("subscribers");
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [stockRows, setStockRows] = useState([]);
  const [savingKey, setSavingKey] = useState("");

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const loadRows = useCallback(async (kind) => {
    setRowsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/list/${kind}`);
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  }, []);

  useEffect(() => { if (authed && tab !== "stock" && tab !== "launch") loadRows(tab); }, [authed, tab, loadRows]);

  const loadStock = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stock`);
      setStockRows(data);
    } catch {
      setStockRows([]);
    }
  }, []);

  useEffect(() => { if (authed && tab === "stock") loadStock(); }, [authed, tab, loadStock]);

  const updateStockValue = (lk, val) => {
    setStockRows((rs) => rs.map((r) => (r.lookup_key === lk ? { ...r, stock: val } : r)));
  };

  const saveStock = async (row) => {
    setSavingKey(row.lookup_key);
    try {
      await axios.post(`${API}/admin/stock`, { lookup_key: row.lookup_key, stock: Number(row.stock) || 0 });
    } finally {
      setSavingKey("");
    }
  };

  const deleteSuggestion = async (id) => {
    try {
      await axios.delete(`${API}/admin/suggestions/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch { /* ignore */ }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`${label} copied to clipboard`);
    } catch {
      alert("Couldn't copy — please copy manually");
    }
  };

  const copyEmails = async (kind, label) => {
    try {
      const { data } = await axios.get(`${API}/admin/list/${kind}`);
      const emails = data.map((r) => r.email).filter(Boolean).join(", ");
      if (!emails) { alert("No emails yet for this list"); return; }
      await copyToClipboard(emails, `${data.length} ${label} email(s)`);
    } catch {
      alert("Couldn't load emails — please try again");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password });
      await loadStats();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your details.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setAuthed(false);
    setStats(null);
  };

  const exportCSV = () => {
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `justjjax-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-sage" size={28} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-warmcream">
        <form onSubmit={handleLogin} className="bg-white rounded-3xl border border-black/5 p-8 w-full max-w-sm shadow-sm" data-testid="admin-login-form">
          <div className="w-12 h-12 rounded-2xl bg-sage/15 flex items-center justify-center mx-auto mb-4">
            <Lock className="text-sage" size={22} />
          </div>
          <h1 className="font-display text-2xl text-center text-black mb-1">Admin</h1>
          <p className="text-charcoal/50 text-sm text-center mb-6">Sign in to view your dashboard</p>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" required
            className="w-full px-4 py-3 rounded-xl border border-ocean/10 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-sage/30"
            data-testid="admin-email-input"
          />
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" required
            className="w-full px-4 py-3 rounded-xl border border-ocean/10 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-sage/30"
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-500 text-sm mb-3" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full bg-olive text-white rounded-full py-3 text-sm font-medium hover:bg-olive/80 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 lg:pt-36 pb-16 bg-warmcream" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-black">Dashboard</h1>
          <button onClick={handleLogout} className="text-charcoal/60 hover:text-black text-sm flex items-center gap-2" data-testid="admin-logout-btn">
            <LogOut size={16} /> Log out
          </button>
        </div>

        {stats && (
          (() => {
            const goal = 15;
            const subs = stats.subscribers || 0;
            const ready = subs >= goal;
            return (
              <div
                className={`rounded-2xl p-5 mb-6 border ${ready ? "bg-olive/10 border-olive/30" : "bg-white border-black/5"}`}
                data-testid="newsletter-goal-banner"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Bell size={18} className={ready ? "text-olive" : "text-sage"} />
                  <p className="font-display text-lg text-black">
                    {ready
                      ? `🎉 You've hit ${subs} sign-ups — time to launch your newsletter!`
                      : `Newsletter launch goal: ${subs} / ${goal} sign-ups`}
                  </p>
                </div>
                <div className="h-2 rounded-full bg-black/5 overflow-hidden">
                  <div
                    className="h-full bg-olive rounded-full transition-all"
                    style={{ width: `${Math.min(100, (subs / goal) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-charcoal/50 mt-2">
                  {ready
                    ? "You've reached the 15-20 target — you're ready to send your first newsletter."
                    : `${goal - subs} more to go until you're ready to start sending.`}
                </p>
              </div>
            );
          })()
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {TABS.map((t) => (
            <div key={t.key} className="bg-white rounded-2xl border border-black/5 p-5" data-testid={`stat-${t.key}`}>
              <t.icon className="text-sage mb-2" size={20} />
              <p className="font-display text-3xl text-black">{stats ? stats[t.statKey] : "—"}</p>
              <p className="text-charcoal/50 text-xs mt-1">{t.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${tab === t.key ? "bg-olive text-white" : "bg-white text-charcoal/60 hover:text-black border border-black/5"}`}
              data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
          <button onClick={() => setTab("stock")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${tab === "stock" ? "bg-olive text-white" : "bg-white text-charcoal/60 hover:text-black border border-black/5"}`}
            data-testid="tab-stock">
            Stock
          </button>
          <button onClick={() => setTab("launch")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-all flex items-center gap-1.5 ${tab === "launch" ? "bg-olive text-white" : "bg-white text-charcoal/60 hover:text-black border border-black/5"}`}
            data-testid="tab-launch">
            <Rocket size={14} /> Launch Kit
          </button>
          {tab !== "stock" && tab !== "launch" && (
            <button onClick={exportCSV} disabled={!rows.length}
              className="ml-auto rounded-full px-4 py-2 text-sm font-medium bg-charcoal text-white hover:bg-black transition-all disabled:opacity-40 flex items-center gap-2"
              data-testid="export-csv-btn">
              <Download size={15} /> Export CSV
            </button>
          )}
        </div>

        {tab === "launch" ? (
          <div className="space-y-5" data-testid="admin-launch-kit">
            <div className="bg-amber/10 border border-amber/20 rounded-2xl p-4 text-sm text-charcoal/70">
              <p className="font-medium text-black mb-1">How to send your launch email</p>
              <p>Copy the draft and the recipient emails below, then paste them into a new email in your own Gmail — put the addresses in the <strong>BCC</strong> field so nobody sees each other's address. This keeps your sending safe and private. For bigger lists later, a dedicated newsletter tool is best.</p>
            </div>
            {Object.entries(LAUNCH_DRAFTS).map(([key, d]) => (
              <div key={key} className="bg-white rounded-2xl border border-black/5 p-5" data-testid={`launch-card-${key}`}>
                <h3 className="font-display text-xl text-black mb-3">{d.label}</h3>
                <p className="text-xs text-charcoal/40 mb-1">Subject</p>
                <p className="text-sm text-black bg-sage/5 rounded-lg px-3 py-2 mb-3">{d.subject}</p>
                <p className="text-xs text-charcoal/40 mb-1">Message</p>
                <pre className="text-sm text-charcoal/70 bg-sage/5 rounded-lg px-3 py-3 mb-4 whitespace-pre-wrap font-sans">{d.body}</pre>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => copyToClipboard(d.subject, "Subject")}
                    className="rounded-full px-4 py-2 text-xs font-medium bg-white border border-black/10 text-charcoal/70 hover:text-black transition-all flex items-center gap-1.5"
                    data-testid={`launch-copy-subject-${key}`}>
                    <Copy size={13} /> Copy subject
                  </button>
                  <button onClick={() => copyToClipboard(d.body, "Message")}
                    className="rounded-full px-4 py-2 text-xs font-medium bg-white border border-black/10 text-charcoal/70 hover:text-black transition-all flex items-center gap-1.5"
                    data-testid={`launch-copy-body-${key}`}>
                    <Copy size={13} /> Copy message
                  </button>
                  <button onClick={() => copyEmails(d.emailsFrom, d.label)}
                    className="rounded-full px-4 py-2 text-xs font-medium bg-olive text-white hover:bg-olive/80 transition-all flex items-center gap-1.5"
                    data-testid={`launch-copy-emails-${key}`}>
                    <Copy size={13} /> Copy all recipient emails
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : tab === "stock" ? (
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid="admin-stock">
            {stockRows.length === 0 ? (
              <p className="py-16 text-center text-charcoal/40 text-sm">Loading products…</p>
            ) : (
              <div className="divide-y divide-black/5">
                {stockRows.map((r) => (
                  <div key={r.lookup_key} className="flex items-center gap-3 px-4 py-3" data-testid={`stock-row-${r.lookup_key}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-black truncate">{r.name}</p>
                      <p className="text-xs text-charcoal/40">{r.brand} · {r.range}</p>
                    </div>
                    <input
                      type="number" min="0" value={r.stock}
                      onChange={(e) => updateStockValue(r.lookup_key, e.target.value)}
                      className="w-20 px-3 py-2 rounded-lg border border-ocean/10 text-sm text-center focus:outline-none focus:ring-2 focus:ring-sage/30"
                      data-testid={`stock-input-${r.lookup_key}`}
                    />
                    <button onClick={() => saveStock(r)} disabled={savingKey === r.lookup_key}
                      className="rounded-full px-4 py-2 text-xs font-medium bg-olive text-white hover:bg-olive/80 transition-all disabled:opacity-50"
                      data-testid={`stock-save-${r.lookup_key}`}>
                      {savingKey === r.lookup_key ? "Saving…" : "Save"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid="admin-table">
          {rowsLoading ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-sage" size={24} /></div>
          ) : rows.length === 0 ? (
            <p className="py-16 text-center text-charcoal/40 text-sm">Nothing here yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-sage/5 text-left text-charcoal/50">
                    {Object.keys(rows[0]).map((c) => (
                      <th key={c} className="px-4 py-3 font-medium whitespace-nowrap">{c}</th>
                    ))}
                    {tab === "suggestions" && <th className="px-4 py-3 font-medium">actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-black/5" data-testid={`row-${tab}-${i}`}>
                      {Object.keys(rows[0]).map((c) => (
                        <td key={c} className="px-4 py-3 text-charcoal/70 align-top">
                          {typeof r[c] === "object" && r[c] !== null ? JSON.stringify(r[c]) : String(r[c] ?? "")}
                        </td>
                      ))}
                      {tab === "suggestions" && (
                        <td className="px-4 py-3 align-top">
                          <button
                            onClick={() => deleteSuggestion(r.id)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                            data-testid={`suggestion-delete-${i}`}
                            aria-label="Delete suggestion"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
